import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import { Div, Text } from '@pinpoint/design-kit';
import { charts, components } from '@pinpoint/onpoint-charts';
import { Message, Button } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { formatNumberV2 } from 'src/functions';

const eventEmitter = new components.EventEmitter();

function Header({ currentQuantiles, quantiles, setQuantiles }) {
  return (
    <Div d="flex" justify="space-between" align="flex-start" m={{ t: '1rem', x: '1rem' }}>
      <Div d="flex" justify="flex-start" align="center" data-test="left-col-title">
        <Text textSize="title" textColor="tabSelected" style={{ fontWeight: 400 }} p={{ r: '1.5rem' }}>
          Lift Calculations
        </Text>
        {currentQuantiles.map((currentQuantile) => (
          <Button
            buttonStyle="tab"
            key={currentQuantile.length}
            selected={quantiles === currentQuantile}
            p={{ x: '1.5em' }}
            w="1.875rem"
            data-tip={CONSTANTS.TAL_MODEL.QUANTILE_LABEL[currentQuantile]}
            onClick={() => {
              setQuantiles(currentQuantile);
            }}
          >
            {currentQuantile}
          </Button>
        ))}
      </Div>
    </Div>
  );
}

Header.propTypes = {
  currentQuantiles: PropTypes.arrayOf(PropTypes.number).isRequired,
  quantiles: PropTypes.number.isRequired,
  setQuantiles: PropTypes.func.isRequired,
};

export default function ActuarialMode({
  modelView: {
    model,
    equalType,
    quantiles,
    quantileIdx,
    availableQuantiles: { current },
    gradient: gradientProp,
  },
  setQuantiles,
  setGradient: setGradientProp,
}) {
  const [gradient, setGradient] = useState(gradientProp);
  const isLegacyActuarial = ['LIFT_VERSION_1_ACT', null, undefined].includes(model?.payload?.version);

  const liftScore = useMemo(() => {
    if (!isLegacyActuarial && equalType === null) return false;
    if (isLegacyActuarial) return get(model, `modelData.dataPayload.lift_scores[${quantileIdx}]`, false);
    if (equalType === 'premiums')
      return (
        model?.modelData?.dataPayload?.equal_premiums?.lift_scores?.[quantileIdx] ||
        model?.modelData?.dataPayload?.equal_premium?.lift_scores?.[quantileIdx] ||
        false
      );
    if (equalType === 'persons')
      return get(model, `modelData.dataPayload.equal_persons.lift_scores[${quantileIdx}]`, false);
    return get(model, `modelData.dataPayload.lift_scores[${quantileIdx}]`, false);
  }, [quantileIdx, isLegacyActuarial, equalType, model]);

  const liftDistribution = get(model, 'modelData.dataPayload.lift_distribution', false);

  const interval = useMemo(() => liftDistribution.slice().sort((a, b) => a.left - b.left), [liftDistribution]);

  const predictionTruncate = model.dataModelType === CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY ? 7 : 2;

  const axisPredictionTruncate = model.dataModelType === CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY ? 3 : 2;

  const predictionHeaderName = useMemo(() => {
    let headerName = '';
    switch (model.dataModelType) {
      case CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY:
        headerName = ' Frequency';
        return headerName;
      case CONSTANTS.TAL_MODEL.TAG.ACT_SEVERITY:
        headerName = ' Severity';
        return headerName;
      case CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_MULT:
        headerName = ' Loss Cost Mult';
        return headerName;
      case CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_DIRECT:
        headerName = ' Loss Cost Direct';
        return headerName;
      default:
        return headerName;
    }
  }, [model.dataModelType]);

  const totalCount = useMemo(() => interval.map(({ count }) => count).reduce((acc, cur) => acc + cur, 0), [interval]);

  const formatTooltip = useCallback(
    ({ data }) => {
      if (!data) return null;
      const customerCount = data[1].toFixed(0);
      const customerPerc = ((data[1] * 100.0) / totalCount).toFixed(2);
      const intervalLeft =
        predictionTruncate === 2
          ? `$${formatNumberV2(parseFloat(data[3]).toFixed(predictionTruncate))}`
          : parseFloat(data[3]).toFixed(predictionTruncate);
      const intervalRight =
        predictionTruncate === 2
          ? `$${formatNumberV2(parseFloat(data[0]).toFixed(predictionTruncate))}`
          : parseFloat(data[0]).toFixed(predictionTruncate);
      const liftMult = (data[2] === 0 || data[2] >= 1 ? data[2] : -1 / data[2]).toFixed(2);
      return `<div style="padding: .75rem 1rem;line-height: 1.4;font-size:1rem">
            Number of Customers: ${customerCount}
            <br />
            Percentage of Customers: ${customerPerc}%
            <br />
            Predicted Loss Ratio Lift: ${liftMult}X
            <br />
            Predicted${predictionHeaderName}: ${intervalLeft} - ${intervalRight}
          </div>`;
    },
    [totalCount, predictionHeaderName, predictionTruncate]
  );

  useEffect(() => {
    setGradient(gradientProp);
  }, [gradientProp]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  if (!liftScore || !interval) {
    return <Message default type="info" text="Analytics unavailable" />;
  }

  return (
    <>
      <Header currentQuantiles={current} quantiles={quantiles} setQuantiles={setQuantiles} />
      <Div m={{ b: '1rem', x: '1rem' }} flexGrow="1" overflow="hidden" data-test="div-inner">
        <charts.Histogram
          dataModelType={model.dataModelType}
          customMultiplier={1}
          intervals={interval}
          countSeries={{
            name: 'Score',
            values: interval ? interval.map(({ count }) => count) : [],
          }}
          liftSeries={{
            name: 'Lift',
            minInterval: liftScore.min_probability,
            values: liftScore.p_lift,
          }}
          onChange={(users, usersPercentage, liftAverage, thinkalikeScoreAverage) => {
            eventEmitter.dispatch('stats', {
              users,
              usersPercentage,
              liftAverage,
              thinkalikeScoreAverage,
            });
          }}
          gradient={gradient}
          onGradientChange={(nextGradient) => {
            setGradient(nextGradient);
            setGradientProp(nextGradient);
          }}
          options={{
            grid: {
              top: '20%',
              bottom: '15%',
            },
            tooltip: {
              formatter: formatTooltip,
            },
            xAxis: {
              name: `Predicted${predictionHeaderName}${predictionTruncate === 3 ? '' : ' ($)'}`,
              type: 'category',
              axisLabel: {
                formatter: (val) =>
                  predictionTruncate === 2
                    ? `${formatNumberV2(Math.round(parseFloat(val)))}`
                    : parseFloat(val).toFixed(axisPredictionTruncate),
              },
            },
          }}
        />
      </Div>
    </>
  );
}

ActuarialMode.propTypes = {
  modelView: PropTypes.shape({
    equalType: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    model: PropTypes.shape({
      name: PropTypes.string,
      payload: PropTypes.shape({ version: PropTypes.string.isRequired }).isRequired,
      dataModelType: PropTypes.string.isRequired,
      modelData: PropTypes.shape({
        dataPayload: PropTypes.shape({
          lift_scores: PropTypes.arrayOf(
            PropTypes.shape({
              percentile: PropTypes.arrayOf(PropTypes.number),
              lift: PropTypes.arrayOf(PropTypes.number),
              total_users: PropTypes.number.isRequired,
              engaged_users: PropTypes.number.isRequired,
            })
          ),
          equal_premiums: PropTypes.oneOf([
            undefined,
            PropTypes.shape({
              lift_scores: PropTypes.arrayOf(
                PropTypes.shape({
                  percentile: PropTypes.arrayOf(PropTypes.number),
                  p_lift: PropTypes.arrayOf(PropTypes.number),
                  total_users: PropTypes.number.isRequired,
                  engaged_users: PropTypes.number.isRequired,
                })
              ),
            }),
          ]),
          equal_premium: PropTypes.oneOf([
            undefined,
            PropTypes.shape({
              lift_scores: PropTypes.arrayOf(
                PropTypes.shape({
                  percentile: PropTypes.arrayOf(PropTypes.number),
                  p_lift: PropTypes.arrayOf(PropTypes.number),
                  total_users: PropTypes.number.isRequired,
                  engaged_users: PropTypes.number.isRequired,
                })
              ),
            }),
          ]),
          equal_persons: PropTypes.oneOf([
            undefined,
            PropTypes.shape({
              lift_scores: PropTypes.arrayOf(
                PropTypes.shape({
                  percentile: PropTypes.arrayOf(PropTypes.number),
                  p_lift: PropTypes.arrayOf(PropTypes.number),
                  total_users: PropTypes.number.isRequired,
                  engaged_users: PropTypes.number.isRequired,
                })
              ),
            }),
          ]),
        }).isRequired,
      }),
    }),
    gradient: PropTypes.string.isRequired,
    fetchModelStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    quantiles: PropTypes.number.isRequired,
    availableQuantiles: PropTypes.shape({
      current: PropTypes.arrayOf(PropTypes.number).isRequired,
      premiums: PropTypes.arrayOf(PropTypes.number),
      persons: PropTypes.arrayOf(PropTypes.number),
    }).isRequired,
    chart: PropTypes.string.isRequired,
    quantileIdx: PropTypes.number.isRequired,
  }).isRequired,
  setQuantiles: PropTypes.func.isRequired,
  gradient: PropTypes.string.isRequired,
  setGradient: PropTypes.func.isRequired,
};
